import React from "react";
import { DocumentRenderer } from "components";
import { Trans } from "react-i18next";
import { calculateTax, CurrencySymbol, formatCurrency, formatDate } from "services/util";
import { TrackingSelect } from "pages/Xero/Settings/ManualJournals/ManualJournalsView";
import {Col, Row, Table} from "reactstrap";
import { getTax } from "components/common/getStatus";
import { keyBy } from "lodash";
import numeral from "numeral";

const LabelAndValue = ({ label, value }) => (
    <>
      <div><strong><Trans>{label}</Trans></strong></div>
      <div>{value}</div>
    </>
  );

const Totals = ({ taxRates, trackingLength, lineAmountType, journalLines }) => {
    const keyedTaxRates = keyBy(taxRates, "TaxType");

    let subtotalDebit = 0;
    let subtotalCredit = 0;
    let totalDebit = 0;
    let totalCredit = 0;
    let tax = [];
    let diff = null;
  
    if (journalLines) {
      journalLines.forEach((line) => {
        const credit = numeral(line.Credit).value() || 0;
        const debit = numeral(line.Debit).value() || 0;
        const taxRate = keyedTaxRates[line.TaxType];
  
        subtotalDebit += debit;
        subtotalCredit += credit;
  
        if (taxRate) {
          const creditTax = calculateTax(credit, taxRate, lineAmountType);
          const debitTax = calculateTax(debit, taxRate, lineAmountType);
  
          [debitTax, creditTax].forEach((taxObj) => {
            taxObj.components.forEach((component) => {
              if (!tax[component.rate]) {
                tax[component.rate] = { debit: 0, credit: 0 };
              }
              tax[component.rate].debit += component.tax;
            });
          });
  
          totalCredit += creditTax.total;
          totalDebit += debitTax.total;
        } else {
          totalDebit += debit;
          totalCredit += credit;
        }
      });
  
      const diffAmount = totalDebit - totalCredit;
      diff = diffAmount !== 0 ? diffAmount : null;
    }  

    const colSpan = trackingLength === 1 ? "3" : trackingLength === 2 ? "4" : "2";
  
    return (
        <tfoot>
        <tr>
          <td colSpan={colSpan}/>
          <td className="text-right"><Trans>Subtotal</Trans></td>
          <td className="text-right">{formatCurrency(subtotalDebit)}</td>
          <td className="text-right">{formatCurrency(subtotalCredit)}</td>
        </tr>
        {tax.map((taxItem) => (
          <tr key={taxItem.rate}>
            <td colSpan={colSpan}/>
            <td className="text-right">
              {lineAmountType === "Inclusive" ? (
                <Trans>Includes Tax at {{ percent: numeral(taxItem.rate).format("0.00%") }}</Trans>
              ) : null}
              {lineAmountType === "Exclusive" ? (
                <Trans>Total Tax at {{ percent: numeral(taxItem.rate).format("0.00%") }}</Trans>
              ) : null}
            </td>
            <td className="text-right">{formatCurrency(taxItem.debit)}</td>
            <td className="text-right">{formatCurrency(taxItem.credit)}</td>
          </tr>
        ))}
        <tr>
          <td colSpan={colSpan}/>
          <td className="text-right"><strong><Trans>TOTAL</Trans></strong></td>
          <td className="text-right"><strong>{formatCurrency(totalDebit)}</strong></td>
          <td className="text-right"><strong>{formatCurrency(totalCredit)}</strong></td>
        </tr>
        {diff !== null && (
          <tr>
            <td colSpan={colSpan}/>
            <td className="text-danger text-right"><Trans>Total is out by:</Trans></td>
            <td className="text-danger text-right"><strong>{formatCurrency(Math.abs(diff))}</strong></td>
          </tr>
        )}
      </tfoot>
    );
  };
    

export const ManualJournalDocument = ({
  item,
  taxRates,
  t,
  trackingCategories,
  accounts,
  organisation,
  taxIncluded = item.LineAmountTypes === "Inclusive" ? true : false,
  ...props
}) => (
  <DocumentRenderer {...props}>
    <div className="container-fluid my-3">
      <div className="top row">
        <div className="d-flex justify-content-between mb-4">
            <h1>
              <Trans>Manual Journal</Trans>
            </h1>
        </div>
      </div>
      <div>
      <Row>
        <Col >
            <LabelAndValue label="Narration" value={item?.Narration || "---"} />
        </Col>
        <Col >
            <LabelAndValue label="Date" value={formatDate(item?.Date) || "---"} />
        </Col>
        <Col >
            <LabelAndValue label="Line Amount Types" value={getTax(item?.LineAmountTypes) || "---"} />
        </Col>
        <Col >
            <LabelAndValue label="Status" value={item?.Status || "---"} />
        </Col>
      </Row>
      </div>
      <Table className="mt-4" size="sm">
          <thead>
              <tr>
                <th className="text-left">
                  <Trans>Description</Trans>
                </th>
                <th className="text-left">
                  <Trans>Account</Trans>
                </th>
                <th className="text-left">
                  <Trans>Tax Rate</Trans>
                </th>
                {trackingCategories?.map((i) => (
                  <th key={i.TrackingCategoryID}><Trans>{i.Name}</Trans></th>
                ))}
                <th>
                  <Trans>Debit</Trans> <Trans><CurrencySymbol currency={organisation?.BaseCurrency}/></Trans>
                </th>
                <th>
                  <Trans>Credit</Trans> <Trans><CurrencySymbol currency={organisation?.BaseCurrency}/></Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {item?.JournalLines?.map((i, idx) => {
                const accountName = accounts?.length > 0 && accounts?.find(option => option.Code === i.AccountCode)?.Name
                const taxName = taxRates?.length > 0 && taxRates?.find(option => option.TaxType === i.TaxType)?.Name
                const credit = i.LineAmount < 0 ? (-1 * i.LineAmount) : "-";
                const debit = i.LineAmount >= 0 ? i.LineAmount : "-" 
                return (
                  <tr key={i.key}>
                    <td className="text-left">{i.Description}</td> 
                    <td className="text-left">{i.AccountCode}{accountName && <> - <span style={{ color: '#525F7F' }}>{accountName}</span></>} </td>
                    <td className="text-left"><span>{taxName}</span><span style={{ color: 'white' }}>{idx + 1}</span></td>
                    {trackingCategories?.map((j) => (
                      <td key={j.TrackingCategoryID}>
                        <Trans>
                        <TrackingSelect
                          lineItem={i}
                          trackingCategory={j}
                          lineItemIndex={idx}
                          disabled
                        /></Trans>
                      </td>
                    ))}
                    <td>{debit > 0 ? formatCurrency(debit) : "-"}</td>
                    <td>{credit > 0 ? formatCurrency(credit) : "-"}</td>
                  </tr>
                );
              })}
            </tbody>
            <Totals taxRates={taxRates} trackingLength={trackingCategories?.length} lineAmountType={item.LineAmountTypes} journalLines={item.JournalLines}/>
          </Table>
    </div>
  </DocumentRenderer>
);

export default ManualJournalDocument;
